import { SearchClient } from 'typesense';
import myShopClient from './nearlive';

const searchCollectionsCache = new Map();

// Org-level callers pass no shopId, so keying on it alone would file every
// organisation's client under the same empty-string entry.
const cacheKeyFor = (organisationId, shopId) => shopId || organisationId;

/**
 * Get Typesense client credentials, scoped to the current shop.
 */
const getSearchKey = async (organisationId, shopId = '') => {
	const client = await myShopClient();
	const { data } = await client.get(`/api/inventory/search-key/${organisationId}${shopId ? `/${shopId}` : ''}`);
	return data;
};

/**
 * Get Typesense client for the current shop.
 *
 * Deliberately not exported — a client held outside `searchCollection` never refreshes its
 * key, which is the shape that caused NEA-5966. Search through `searchCollection` instead.
 *
 * @param {string} organisationId
 * @param {string} [shopId]
 * @return {Promise<import('typesense/lib/Typesense/Documents').SearchableDocuments>}
 */
const getSearchCollection = async (organisationId, shopId = '') => {
	const cacheKey = cacheKeyFor(organisationId, shopId);
	if (searchCollectionsCache.has(cacheKey)) return searchCollectionsCache.get(cacheKey);

	const { host, apiKey, collectionName } = await getSearchKey(organisationId, shopId);

	const searchClient = new SearchClient({
		nodes: [{ host, port: 443, protocol: 'https' }],
		apiKey
	});
	const collection = searchClient.collections(collectionName).documents();

	searchCollectionsCache.set(cacheKey, collection);

	return collection;
};

// typesense v2 drops `httpStatus` on the mapped 4xx errors — its 401 branch calls
// `new RequestUnauthorized(message)` without the status, unlike v1 which assigned
// `error.httpStatus` afterwards. Check all three so this holds across both majors and
// survives a bundler renaming the class (which would take `name` with it).
const isUnauthorised = (error) =>
	error?.httpStatus === 401 || error?.name === 'RequestUnauthorized' || /HTTP code 401\b/.test(error?.message ?? '');

/**
 * Search the shop's Typesense collection, refreshing the search key if it has expired.
 *
 * Scoped search keys are issued with a 24 hour expiry, so a dashboard tab left open
 * overnight holds a client Typesense rejects with a 401. Typesense doesn't retry 4xx
 * itself, so drop the cached client and try once more with a freshly issued key.
 *
 * @param {string} organisationId
 * @param {string} [shopId]
 * @param {object} searchParameters
 * @return {Promise<import('typesense/lib/Typesense/Documents').SearchResponse>}
 */
export const searchCollection = async (organisationId, shopId = '', searchParameters) => {
	const collection = await getSearchCollection(organisationId, shopId);

	try {
		return await collection.search(searchParameters, {});
	} catch (error) {
		if (!isUnauthorised(error)) throw error;

		searchCollectionsCache.delete(cacheKeyFor(organisationId, shopId));
		const refreshedCollection = await getSearchCollection(organisationId, shopId);

		return refreshedCollection.search(searchParameters, {});
	}
};

/**
 * Parse filters from an object to a string for Typesense,
 * removing conflicting filters.
 *
 * @param {Record<string, boolean>} filters
 * @param {boolean} [isOrganisationStock=false]
 * @returns {string}
 */
export const parseFilters = (filters, isOrganisationStock = false) => {
	let parsedFilters = [];

	const enabledFilters = Object.entries(filters)
		.filter(([, value]) => value)
		.map(([key]) => key);

	const andFilters = enabledFilters.filter((filter) => !filter.startsWith('delivery_categories:='));
	for (const filter of andFilters) {
		if (filter === 'enabled_i:=true && has_errors:!=true' && enabledFilters.includes('enabled_i:=false')) continue;
		if (filter === 'enabled_i:=false' && enabledFilters.includes('enabled_i:=true && has_errors:!=true')) continue;
		if (filter === 'in_stock:=true' && enabledFilters.includes('in_stock:=false')) continue;
		if (filter === 'in_stock:=false' && enabledFilters.includes('in_stock:=true')) continue;
		parsedFilters.push(filter);
	}

	// Delivery categories are OR filters, so we need to group them together
	const deliveryCategoriesFilters = enabledFilters.filter((filter) => filter.startsWith('delivery_categories:='));
	if (deliveryCategoriesFilters.length) {
		const deliveryCategoriesFilter = deliveryCategoriesFilters.join(' || ');
		parsedFilters.push(`(${deliveryCategoriesFilter})`);
	}

	// In organisation stock, we have slightly different field names
	if (isOrganisationStock) {
		parsedFilters = parsedFilters.map((filter) => {
			filter = filter.replace('enabled_i', 'enabled');
			filter = filter.replace('productSource_i', 'productSource');
			return filter;
		});
	}

	return parsedFilters.join(' && ');
};

/**
 * Search inventory for a shop.
 * @param {object} params
 * @param {string} params.organisationId
 * @param {string} [params.shopId]
 * @param {number} params.page
 * @param {string} params.query
 * @param {Record<string, boolean>} params.filters
 * @returns {Promise<{ inventory: any[], numOfPages: number }>}
 */
export const listInventory = async ({ organisationId, shopId = '', page, query, filters }) => {
	const data = await searchCollection(organisationId, shopId, {
		q: query || '*',
		filter_by: parseFilters(filters, !shopId),
		query_by: 'title, barcode, category_titles, delivery_categories',
		facet_by: 'category_ids, delivery_categories',
		sort_by: 'barcode:asc',
		per_page: 50,
		page
	});

	const inventory = data.hits.map(({ document }) => ({
		...document,
		enabled: document.enabled_i,
		productSource: document.productSource_i
	}));

	return {
		inventory,
		numOfPages: Math.ceil(data.found / 50)
	};
};

/**
 * Get existing delivery categories for a shop.
 * @param {object} params
 * @param {string} params.organisationId
 * @param {string} [params.shopId]
 * @returns {Promise<{ value: string, label: string }[]>}
 */
export const listDeliveryCategories = async ({ organisationId, shopId = '' }) => {
	const data = await searchCollection(organisationId, shopId, {
		q: shopId || organisationId,
		query_by: shopId ? 'shopId' : 'organisation_id',
		facet_by: 'delivery_categories',
		page: 1,
		per_page: 0,
		max_facet_values: 1000
	});

	return data.facet_counts[0].counts.map(({ value }) => ({
		value,
		label: value
	}));
};
